import React, { useEffect, useRef, useState } from 'react';
import { useSeoTitle } from '../lib/seo';
import axios from 'axios';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { FileText, ChevronRight, CalendarDays, Bell, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { DevotionalDayCard, Label, dayWord } from '../components/DevotionalDayCard';
import { ShareButtons } from '../components/ShareButtons';
import { ReadingStreak } from '../components/ReadingStreak';
import { useReadingStreak } from '../hooks/useReadingStreak';
import { NotesDownload } from '../components/NotesDownload';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : '';

export default function Devotional() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [dev, setDev] = useState(null);
  useSeoTitle(slug && dev?.title ? dev.title : '', dev?.subtitle || undefined);
  const [archive, setArchive] = useState([]);
  const [status, setStatus] = useState('loading');
  const scrolled = useRef(false);

  useEffect(() => {
    setStatus('loading');
    scrolled.current = false;
    const url = slug ? `${API}/api/devotionals/${slug}` : `${API}/api/devotionals/current`;
    axios.get(url)
      .then((r) => { setDev(r.data); setStatus('ok'); })
      .catch(() => setStatus('empty'));
    axios.get(`${API}/api/devotionals`).then((r) => setArchive(r.data.devotionals || [])).catch(() => {});
  }, [slug]);

  const { markRead, isRead, stats } = useReadingStreak(dev?.slug || slug || 'current', dev?.days?.length || 7);
  const onRead = React.useCallback((n) => {
    markRead(n);
    if (dev?.slug) axios.post(`${API}/api/devotionals/${dev.slug}/days/${n}/read`, null, { withCredentials: true }).catch(() => {});
  }, [markRead, dev?.slug]);

  const todayNum = dev?.days?.find((d) => d.date === dev.today)?.day_number;
  const focusDay = Number(params.get('day')) || todayNum;

  useEffect(() => {
    if (!dev || scrolled.current || !params.get('day')) return;
    const el = document.getElementById(`day-${params.get('day')}`);
    if (el) {
      scrolled.current = true;
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [dev, params]);

  return (
    <div className="min-h-screen bg-[#f6f5f0] pt-24" data-testid="devotional-page">
      <section className="py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          {status === 'ok' && (
            <>
              <Label className="flex items-center justify-center gap-3 !text-[#1f4d36]">
                <span className="h-px w-8 bg-[#b8923a]" /> A {dayWord(dev.days.length)}-Day Devotional <span className="h-px w-8 bg-[#b8923a]" />
              </Label>
              {dev.cover_image_url && (
                <img
                  src={dev.cover_image_url.startsWith('http') ? dev.cover_image_url : `${API}${dev.cover_image_url}`}
                  alt={`${dev.title} — weekly devotional cover from Life Church of New Jersey`}
                  className="mx-auto mt-8 max-h-[380px] w-auto max-w-full rounded-sm shadow-xl shadow-green-900/10 object-contain"
                  data-testid="devotional-cover-image"
                />
              )}
              <h1 className="font-serif font-bold text-5xl sm:text-6xl lg:text-7xl text-[#1f4d36] mt-8 leading-[1.05]" data-testid="devotional-title">{dev.title}</h1>
              {dev.subtitle && <p className="font-serif italic text-[#b8923a] text-lg md:text-xl mt-4" data-testid="devotional-subtitle">{dev.subtitle}</p>}
              {dev.key_verse_text && (
                <blockquote className="mt-12 max-w-xl mx-auto" data-testid="devotional-key-verse">
                  <p className="font-serif italic text-slate-700 text-base md:text-lg leading-relaxed">{dev.key_verse_text}</p>
                  {dev.key_verse_reference && <cite className="not-italic block text-[11px] font-bold tracking-[0.25em] uppercase text-[#1f4d36] mt-3">{dev.key_verse_reference}</cite>}
                </blockquote>
              )}
              <p className="text-slate-500 mt-10 text-sm flex items-center justify-center gap-2">
                <CalendarDays className="w-4 h-4" /> Week of {fmtDate(dev.week_start)}
              </p>
              <div className="flex flex-wrap justify-center gap-3 mt-7">
                {dev.pdf_url && (
                  <a href={`${API}${dev.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid="devotional-pdf-link">
                    <Button variant="outline" className="border-[#1f4d36] text-[#1f4d36] hover:bg-[#1f4d36]/5 rounded-none">
                      <FileText className="w-4 h-4 mr-2" /> View original PDF
                    </Button>
                  </a>
                )}
                {todayNum && (
                  <a href={`#day-${todayNum}`} data-testid="devotional-today-link">
                    <Button className="bg-[#1f4d36] hover:bg-[#173b29] rounded-none">
                      <Sparkles className="w-4 h-4 mr-2" /> Jump to today (Day {todayNum})
                    </Button>
                  </a>
                )}
              </div>
              <div className="mt-14 pt-6 border-t border-slate-300/70">
                <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-slate-500">Life Church of New Jersey</p>
                <p className="font-serif italic text-[#b8923a] text-sm mt-1">New Life Starts Here</p>
              </div>
            </>
          )}
          {status === 'empty' && (
            <div className="mt-6" data-testid="devotional-empty">
              <h1 className="font-serif text-4xl text-[#1f4d36]">A new devotional is coming soon</h1>
              <p className="text-slate-600 mt-3 max-w-xl mx-auto">Check back shortly — and turn on notifications to get each day's reading delivered to you at 8 AM.</p>
            </div>
          )}
          {status === 'loading' && <div className="h-40 mt-4 rounded bg-slate-200/60 animate-pulse" />}
        </div>
      </section>

      {status === 'ok' && (dev.intro || dev.how_to_use?.length > 0) && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-4 space-y-10">
          {dev.intro && (
            <div className="bg-white border border-slate-200/80 px-6 py-10 sm:px-12 sm:py-12" data-testid="devotional-intro">
              <Label className="mb-4">{dev.title}</Label>
              <div className="font-serif text-slate-800 text-lg md:text-xl leading-relaxed space-y-4">
                {dev.intro.split(/\n{2,}|\n/).filter((x) => x.trim()).map((x, i) => <p key={i}>{x.trim()}</p>)}
              </div>
            </div>
          )}
          {dev.how_to_use?.length > 0 && (
            <div className="bg-white border border-slate-200/80 px-6 py-10 sm:px-12 sm:py-12" data-testid="devotional-how-to">
              <Label className="mb-2">Before You Begin</Label>
              <h2 className="font-serif font-bold text-2xl md:text-3xl text-[#1f4d36]">How To Use These {dayWord(dev.days.length).charAt(0) + dayWord(dev.days.length).slice(1).toLowerCase()} Days</h2>
              <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-7">
                {dev.how_to_use.map((h, i) => (
                  <div key={i}>
                    <Label className="mb-1.5 !text-[#1f4d36]">{h.label}</Label>
                    <p className="text-slate-700 leading-relaxed text-sm md:text-base">{h.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {status === 'ok' && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-20 pt-10">
          <div className="mb-6">
            <ReadingStreak stats={stats} days={dev.days} isRead={isRead} todayNum={todayNum} />
            <div className="mt-3 flex flex-wrap items-center justify-end gap-3" data-testid="devotional-notes-export">
              <span className="text-xs text-slate-500">Your notes stay on this device — keep a copy anytime.</span>
              <NotesDownload slug={dev.slug} days={dev.days} />
            </div>
          </div>

          {(() => {
            const today = dev.days.find((d) => d.day_number === todayNum);
            const share = today || dev.days[0];
            return (
              <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50/70 p-5 flex flex-col gap-4" data-testid="devotional-share-today">
                <div className="min-w-0">
                  <p className="text-xs uppercase tracking-[0.18em] font-bold text-church-green">{today ? "Share today's devotional" : 'Share this devotional'}</p>
                  <p className="text-sm text-slate-700 mt-1 truncate">Day {share.day_number}: {share.title}{share.scripture ? ` · ${share.scripture}` : ''}</p>
                </div>
                <ShareButtons
                  url={today ? `${API}/api/share/devotional/${dev.slug}?day=${share.day_number}` : `${API}/api/share/devotional/${dev.slug}`}
                  title={`${dev.title} · Day ${share.day_number}: ${share.title}`}
                  text={share.scripture}
                  imageUrl={`${API}/api/share/devotional/${dev.slug}/card.png?day=${share.day_number}&format=story`}
                  testId="share-today"
                />
              </div>
            );
          })()}

          <nav className="flex gap-2 overflow-x-auto pb-3 mb-8 -mx-1 px-1" aria-label="Days" data-testid="devotional-day-nav">
            {dev.days.map((d) => (
              <a
                key={d.day_number}
                href={`#day-${d.day_number}`}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm border transition-colors ${d.day_number === focusDay ? 'bg-[#1f4d36] text-white border-[#1f4d36]' : 'border-slate-300 text-slate-600 hover:border-[#1f4d36] hover:text-[#1f4d36] bg-white'}`}
                data-testid={`devotional-day-tab-${d.day_number}`}
              >
                Day {d.day_number}{isRead(d.day_number) ? ' ✓' : ''}
              </a>
            ))}
          </nav>
          <div className="space-y-10">
            {dev.days.map((d) => (
              <DevotionalDayCard
                key={d.day_number}
                day={d}
                slug={dev.slug}
                isToday={d.day_number === todayNum}
                isFocus={d.day_number === focusDay}
                shareUrl={`${API}/api/share/devotional/${dev.slug}?day=${d.day_number}`}
                shareImageUrl={`${API}/api/share/devotional/${dev.slug}/card.png?day=${d.day_number}&format=story`}
                devotionalTitle={dev.title}
                isRead={isRead(d.day_number)}
                onRead={onRead}
              />
            ))}
          </div>

          <div className="mt-14 rounded-2xl bg-green-50 border border-green-100 p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="devotional-push-cta">
            <Bell className="w-8 h-8 text-church-green shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-slate-900">Get each day delivered at 8 AM</p>
              <p className="text-sm text-slate-600">Allow notifications and we'll send you the day's reading every morning — plus a gentle evening reminder if you haven't had a chance to read yet.</p>
            </div>
          </div>

          {archive.filter((a) => a.slug !== dev.slug).length > 0 && (
            <div className="mt-12" data-testid="devotional-archive">
              <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-3">Past devotionals</h2>
              <ul className="divide-y border rounded-xl">
                {archive.filter((a) => a.slug !== dev.slug).map((a) => (
                  <li key={a.id}>
                    <Link to={`/devotional/${a.slug}`} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-sm" data-testid={`devotional-archive-${a.slug}`}>
                      <span><span className="font-medium text-slate-900">{a.title}</span> <span className="text-slate-400">· week of {fmtDate(a.week_start)}</span></span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}
      <Footer />
    </div>
  );
}
